import { useEffect, useMemo, useReducer } from 'react';
import { message } from 'antd';
import { lookUp } from '../../api/LookUpAPI';
import axiosInstance from '../../api/AxiosInstance';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import medicationsByCategory from './medications-by-category';

// Constants
const MAX_MIF_HISTORY_ITEMS = 10;

// Helper function to extract error message from API response
const extractErrorMessage = (error, fallbackMessage = 'An unexpected error occurred') => {
  // Check for validation errors array
  if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
    return error.response.data.errors
      .map(err => err.msg || err.message || 'Validation error')
      .join(', ');
  }
  
  // Check for single error message
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Check for error string
  if (error.response?.data?.error && typeof error.response.data.error === 'string') {
    return error.response.data.error;
  }
  
  // Check for network errors
  if (error.message === 'Network Error') {
    return 'Network error. Please check your connection and try again.';
  }
  
  // Check for timeout
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }
  
  // Use fallback
  return fallbackMessage;
};

// Reducer for managing component state
const initialState = {
  loading: false,
  loadingCategories: true,
  categories: [],
  loadingStates: true,
  states: [],
  selectedCategory: null,
  selectedState: null,
  availableMedications: [],
};

const dataGeneratorReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_LOADING_CATEGORIES':
      return { ...state, loadingCategories: action.payload };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_LOADING_STATES':
      return { ...state, loadingStates: action.payload };
    case 'SET_STATES':
      return { ...state, states: action.payload };
    case 'SET_SELECTED_CATEGORY':
      return { ...state, selectedCategory: action.payload };
    case 'SET_SELECTED_STATE':
      return { ...state, selectedState: action.payload };
    case 'SET_AVAILABLE_MEDICATIONS':
      return { ...state, availableMedications: action.payload };
    case 'RESET':
      return {
        ...initialState,
        categories: state.categories,
        states: state.states,
        loadingCategories: false,
        loadingStates: false,
      };
    default:
      return state;
  }
};

export const useDataGenerator = () => {
  const [state, dispatch] = useReducer(dataGeneratorReducer, initialState);

  // localStorage for MIF history, category usage, last response, and last used category
  const [mifHistory, updateMifHistory] = useLocalStorage('dataGenerator:mifHistory', []);
  const [categoryUsage, updateCategoryUsage] = useLocalStorage('dataGenerator:categoryUsage', {});
  const [stateUsage, updateStateUsage] = useLocalStorage('dataGenerator:stateUsage', {});
  const [lastResponse, updateLastResponse] = useLocalStorage('dataGenerator:lastResponse', null);
  const [lastUsedCategory, updateLastUsedCategory] = useLocalStorage('dataGenerator:lastUsedCategory', null);
  const [lastUsedState, updateLastUsedState] = useLocalStorage('dataGenerator:lastUsedState', null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        dispatch({ type: 'SET_LOADING_CATEGORIES', payload: true });
        const categoryResponse = await lookUp('tele-medicine-category');
        if (isMounted && categoryResponse.data && categoryResponse.data.payload) {
          dispatch({ 
            type: 'SET_CATEGORIES', 
            payload: categoryResponse.data.payload.filter(category => category.oneClickPrescribeEligible) || []
          });
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching categories:', error);
          message.error('Failed to load categories');
        }
      } finally {
        if (isMounted) {
          dispatch({ type: 'SET_LOADING_CATEGORIES', payload: false });
        }
      }

      try {
        dispatch({ type: 'SET_LOADING_STATES', payload: true });
        const stateResponse = await lookUp('state');
        if (isMounted && stateResponse.data && stateResponse.data.payload) {
          dispatch({ 
            type: 'SET_STATES', 
            payload: stateResponse.data.payload.filter(state => state.oneClickPrescribeEligible) || []
          });
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching states:', error);
          message.error('Failed to load states');
        }
      } finally {
        if (isMounted) {
          dispatch({ type: 'SET_LOADING_STATES', payload: false });
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);


  // Auto-select last used category when categories are loaded
  useEffect(() => {
    if (state.categories.length > 0 && lastUsedCategory && !state.selectedCategory) {
      const category = state.categories.find(c => c.name === lastUsedCategory);
      if (category) {
        dispatch({ type: 'SET_SELECTED_CATEGORY', payload: category });
        
        // Load medications for this category
        const categoryMeds = medicationsByCategory.find(m => 
          m._id.category.toLowerCase() === lastUsedCategory.toLowerCase()
        );
        
        if (categoryMeds && categoryMeds.medicine) {
          const medsWithNdc = categoryMeds.medicine.filter(med => med.ndc);
          dispatch({ type: 'SET_AVAILABLE_MEDICATIONS', payload: medsWithNdc });
        }
      }
    }
  }, [state.categories, lastUsedCategory, state.selectedCategory]);

  useEffect(() => {
    if (state.states.length > 0 && lastUsedState && !state.selectedState) {
      const stateDoc = state.states.find(s => s.name === lastUsedState);
      if (stateDoc) {
        dispatch({ type: 'SET_SELECTED_STATE', payload: stateDoc });
      }
    }
  }, [state.states, lastUsedState, state.selectedState]);

  
  const handleCategoryChange = (categoryName, form) => {
    const category = state.categories.find(c => c.name === categoryName);
    dispatch({ type: 'SET_SELECTED_CATEGORY', payload: category });
    
    // Find medications for this category
    const categoryMeds = medicationsByCategory.find(m => 
      m._id.category.toLowerCase() === categoryName.toLowerCase()
    );
    
    if (categoryMeds && categoryMeds.medicine) {
      // Filter to only medications with NDC codes
      const medsWithNdc = categoryMeds.medicine.filter(med => med.ndc);
      dispatch({ type: 'SET_AVAILABLE_MEDICATIONS', payload: medsWithNdc });
    } else {
      dispatch({ type: 'SET_AVAILABLE_MEDICATIONS', payload: [] });
    }
    
    // Reset medication field when category changes
    if (form) {
      form.setFieldsValue({ medication: undefined });
    }

    // Increment category usage count
    const newUsage = { ...categoryUsage };
    newUsage[categoryName] = (newUsage[categoryName] || 0) + 1;
    updateCategoryUsage(newUsage);

    // Save as last used category
    updateLastUsedCategory(categoryName);
  };

  const handleStateChange = (stateName, form) => {
    const stateDoc = state.states.find(s => s.name === stateName);
    dispatch({ type: 'SET_SELECTED_STATE', payload: stateDoc });
    
    // Increment state usage count
    const newUsage = { ...stateUsage };
    newUsage[stateName] = (newUsage[stateName] || 0) + 1;
    updateStateUsage(newUsage);

    // Save as last used state
    updateLastUsedState(stateName);
  };

  // Sort categories by usage count, then alphabetically
  const sortedCategories = useMemo(() => {
    return [...state.categories].sort((a, b) => {
      const usageA = categoryUsage[a.name] || 0;
      const usageB = categoryUsage[b.name] || 0;
      
      // Sort by usage count (descending)
      if (usageB !== usageA) {
        return usageB - usageA;
      }
      
      // If usage is the same, sort alphabetically
      return a.name.localeCompare(b.name);
    });
  }, [state.categories, categoryUsage]);

  // Sort states by usage count, then alphabetically
  const sortedStates = useMemo(() => {
    return [...state.states].sort((a, b) => {
      const usageA = stateUsage[a.name] || 0;
      const usageB = stateUsage[b.name] || 0;
      
      // Sort by usage count (descending)
      if (usageB !== usageA) {
        return usageB - usageA;
      }
      
      // If usage is the same, sort alphabetically
      return a.name.localeCompare(b.name);
    });
  }, [state.states, stateUsage]);

  // Transform categories into Select options format
  const categoryOptions = sortedCategories.map(category => ({
    label: category.name,
    value: category.name,
    key: category._id,
  }));
  
  const stateOptions = sortedStates.map(state => ({
    label: state.name,
    value: state.name,
    key: state._id,
  }));

  // Transform medications into Select options format
  const medicationOptions = state.availableMedications.map((med, idx) => ({
    label: `${med.drug} - ${med.strength} (NDC: ${med.ndc})`,
    value: `${med.drug}|${idx}`,
  }));

  const handleSubmit = async (values) => {
    // Prevent duplicate submissions
    if (state.loading) {
      return { success: false };
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // Parse patient data object (supports both JSON and JavaScript object literals)
      let patientData;
      try {
        if (typeof values.patientData === 'string') {
          // Try JSON.parse first
          try {
            patientData = JSON.parse(values.patientData);
          } catch (jsonError) {
            // If JSON.parse fails, try to evaluate as JavaScript object literal
            try {
              patientData = new Function('return (' + values.patientData + ')')();
            } catch (evalError) {
              throw new Error('Must be a valid JavaScript object or JSON');
            }
          }
        } else {
          patientData = values.patientData;
        }
      } catch (e) {
        message.error(`Invalid patient data format: ${e.message}`);
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false };
      }

      // Find the selected medication
      const selectedMed = state.availableMedications.find(
        (med, idx) => `${med.drug}|${idx}` === values.medication
      );

      if (!selectedMed) {
        message.error('Please select a valid medication');
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false };
      }

      // Parse height in inches and calculate heightFt and heightIn
      let heightInInches = patientData.height;
      if (typeof heightInInches === 'string') {
        // Remove any non-numeric characters except decimal point
        heightInInches = parseFloat(heightInInches.replace(/[^\d.]/g, ''));
      }
      heightInInches = parseInt(heightInInches, 10);
      
      const heightFt = Math.floor(heightInInches / 12);
      const heightIn = heightInInches % 12;

      // Build payload with patient data
      const payload = {
        telemedicinecategory: values.telemedicinecategory,
        state: values.state,
        client: 'Rex MD',
        // Include all patient data fields
        // mif should already be a stringified JSON array
        mif: patientData.mif,
        medications: patientData.medications || '',
        allergies: patientData.allergies || '',
        bloodPressure: patientData.bloodPressure || '',
        age: parseInt(patientData.age, 10),
        gender: patientData.gender,
        weight: patientData.weight,
        heightFt,
        heightIn,
        // Medication details from selection
        requestedMedicines: [
          {
            drug_name: selectedMed.drug,
            dosage: selectedMed.strength,
            dispense: 'Tablets',
            refills: selectedMed.refills,
            days_supply: selectedMed.daysSupply,
            ndc: selectedMed.ndc,
            pillCount: selectedMed.quantity,
            daw: false,
          }
        ]
      };

      // Make request (authentication handled by axios instance)
      const response = await axiosInstance.post('/cases/generate-test', payload);

      const caseId = response.data?.payload?.case?._id || response.data?.payload?._id || response.data?._id || 'Unknown';
      
      // Determine evaluation status from response
      const caseData = response.data?.payload?.case || response.data?.payload || response.data;
      let evaluationStatus = 'not-evaluated';
      
      if (caseData?.tags && Array.isArray(caseData.tags) && caseData.tags.includes('*CLEAN_MIF')) {
        evaluationStatus = 'passed';
      } else if (caseData?.oneClickPrescribeAssessment) {
        evaluationStatus = 'failed';
      }
      
      // Show success message
      message.success(`Case created successfully! Case ID: ${caseId}. Check the API Response section below or navigate to /cases/${caseId} to view.`, 10);
      
      // Store the response in state and localStorage
      updateLastResponse(response.data);
      
      // Store patient data in history with evaluation status
      saveMifToHistory(values.telemedicinecategory, values.patientData, evaluationStatus);
      
      return { success: true, caseId, response: response.data };
    } catch (error) {
      console.error('Error creating case:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to create test case. Please try again.');
      message.error(errorMessage);
      
      // Store the error response in the API Response textarea
      const errorResponse = {
        success: false,
        error: errorMessage,
        statusCode: error.response?.status,
        fullError: error.response?.data || { message: errorMessage }
      };
      updateLastResponse(errorResponse);
      
      return { success: false, error: errorMessage };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const saveMifToHistory = (categoryName, patientData, evaluationStatus = 'not-evaluated') => {
    const dataString = typeof patientData === 'string' ? patientData : JSON.stringify(patientData);
    const newHistory = [...mifHistory];
    
    // Check if this patient data already exists
    const existingIndex = newHistory.findIndex(
      item => item.category === categoryName && item.mif === dataString
    );
    
    if (existingIndex === -1) {
      // Add to beginning of array
      newHistory.unshift({
        category: categoryName,
        mif: dataString, // Keep the property name for backwards compatibility
        timestamp: Date.now(),
        evaluationStatus,
      });
      
      // Keep only the last MAX_MIF_HISTORY_ITEMS MIFs
      if (newHistory.length > MAX_MIF_HISTORY_ITEMS) {
        newHistory.pop();
      }
      
      updateMifHistory(newHistory);
    } else {
      // Update timestamp and evaluation status of existing entry and move to front
      const existingItem = newHistory.splice(existingIndex, 1)[0];
      existingItem.timestamp = Date.now();
      existingItem.evaluationStatus = evaluationStatus;
      newHistory.unshift(existingItem);
      updateMifHistory(newHistory);
    }
  };

  const loadMifFromHistory = (historyItem, form) => {
    if (form) {
      form.setFieldsValue({ patientData: historyItem.mif }); // property is still called 'mif' in storage
      message.success('Patient data loaded from history');
    }
  };

  const clearMifHistory = () => {
    updateMifHistory([]);
    message.success('Patient data history cleared');
  };

  const clearCategoryUsage = () => {
    updateCategoryUsage({});
    message.success('Category usage cleared');
  };

  const clearStateUsage = () => {
    updateStateUsage({});
    message.success('State usage cleared');
  };

  const resetState = (form) => {
    dispatch({ type: 'RESET' });
    updateLastResponse(null);
    if (form) {
      form.resetFields();
    }
  };

  // Get MIF history for current category
  const currentCategoryMifs = useMemo(() => {
    if (!state.selectedCategory) return [];
    return mifHistory.filter(item => item.category === state.selectedCategory.name);
  }, [mifHistory, state.selectedCategory]);

  return {
    loading: state.loading,
    loadingCategories: state.loadingCategories,
    categories: state.categories,
    selectedCategory: state.selectedCategory,
    loadingStates: state.loadingStates,
    states: state.states,
    selectedState: state.selectedState,
    stateOptions,
    availableMedications: state.availableMedications,
    categoryOptions,
    medicationOptions,
    mifHistory,
    currentCategoryMifs,
    categoryUsage,
    lastResponse,
    handleCategoryChange,
    handleStateChange,
    handleSubmit,
    resetState,
    loadMifFromHistory,
    clearMifHistory,
    clearCategoryUsage,
    clearStateUsage,
  };
};
